/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { FancyAction } from 'src/app/common/fancy-firebase/base/FancyAction';
import { FancyActionableFirebase } from 'src/app/common/fancy-firebase/generics/FancyActionableFirebase';
import { ProjectMemberList } from 'src/app/common/fancy-firebase/services/factories';
import { ProjectMember } from '@touchplan/common';
import { User } from '@touchplan/common';
import { Observable, combineLatest, of } from 'rxjs';
import { switchMap, filter, map } from 'rxjs/operators';

export class ProjectMembersApi extends FancyActionableFirebase<
	ProjectMember,
	FancyAction,
	ProjectMemberList
> {
	constructor(public data: ProjectMemberList) {
		super(data);
	}

	/**
	 * no support for delayed persistence due to the special write, it _could_ be added but this is temporary code, so...probably not worth
	 * Stated differently, projectMembers _always_ persist imediately
	 */
	public addItem(initialData: object) {
		throw new Error('not in use, call the cloud function');
	}

	public isAdmin(userId: string): boolean {
		return Array.from(this.data.syncList.values()).some(
			(projectMember) => projectMember.view.userId === userId && projectMember.view.accessLevel === 'Admin'
		);
	}
}

/**
 * Accepts a user and projectMemberApi observable and returns an observable representing the projectMember for that user within
 * the passed in projectMembers api. Responds dynamically when either the projectMember api or the user is switched.
 * This code has two main usage paths, "I need to always have the most relavant projectMember data across multiple state changes". For
 * example, always having "your" projectMember data as relevant to the current selected project.
 * See ProjectState.authedProjectMember$
 * The other path is simpler, "I need to listen to the projectMember data for x user on this project". This looks something like
 * ```
 * userProjectMemberObserver(appState.user, of(listProjectMemberApis.get(projectId)))
 * ```
 * There's also the flexibility here for switching users if such a thing ever comes up.
 */
export function userProjectMemberObserver(
	user: Observable<User>,
	pmApi: Observable<ProjectMembersApi>
): Observable<ProjectMember> {
	return combineLatest([user, pmApi]).pipe(
		switchMap((combination) => {
			if (!combination[0] || !combination[1]) {
				return of(null);
			}
			return combination[1].data.safeRawEvent$().pipe(
				filter((action) => combination[0].$id === action.payload.view.userId),
				map((action) => action.payload)
			);
		})
	);
}
